import { CommonDialogs } from "@mcleod/common";
import { Button, Checkbox, ClickEvent, DataSourceMode, EditRowDecorator, Image, Layout, SaveButton, SelectionType, Snackbar, Table, TableRow, TableRowDisplayEvent, TableSelectionEvent, Textbox, Toast } from "@mcleod/components";
import { Api, Block, DOMUtil, ModelRow, StringUtil, UrlUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutTariffLineItem } from "./autogen/AutogenLayoutTariffLineItem";
import { FilterPanel, SecondaryFilterProps } from "./planning/FilterPanel";
import { TariffManagementTariffLineItem } from "./TariffManagementTariffLineItem";
import { TariffManagementTLIAdjustRates } from "./TariffManagementTLIAdjustRates";
import { TariffManagementUtil } from "./TariffManagementUtil";

export class TariffLineItem extends AutogenLayoutTariffLineItem {
    public tariffLineItemPanel: FilterPanel;
    public tariffLineItemSecondaryFilters: SecondaryFilterProps[] = TariffManagementUtil.getOrdersSecondaryFilters();
    private adjustRateTLISlideoutLayout = Layout.getLayout("lme/dispatch/TariffManagementTLIAdjustRates") as TariffManagementTLIAdjustRates;
    private editTariffLineItemImage: Image;
    private copyTariffLineItemImage: Image;
    private adjustRatesTariffLineItemImage: Image;
    private buttonAddTariffLineItem: Button;
    private _tliUid: any;

    override onLoad() {
        this.setURLParams();
        this.setupTariffLineItemTable();
        TariffManagementUtil.setTableColumnAlignmentToLeft(this.tableTariffLineItem);
        this.enableActionItemImageAndScrollElementIntoView();
        this.showEmptyColumns();
        this.mainDataSource.orderBy = [{ field: "gla_uid", sort: "desc" }];
    }

    private setURLParams() {
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        if (urlParams?.tli_uid != null) {
            this._tliUid = urlParams.tli_uid;
        }
    }

    setupTariffLineItemTable() {
        const defaultCopyTLITooltip = "Copy selected Tariff Line Items";
        const defaultAdjustTLITooltip = "Adjust rates for selected Tariff Line Items";
        this.tariffLineItemPanel = new FilterPanel(this.tariffLineItemSecondaryFilters, this.tableTariffLineItem, this.panelTariffLineItemTask);
        this.tableTariffLineItem.addSelectionListener(() => this.updateImageTooltipAndState(defaultCopyTLITooltip, defaultAdjustTLITooltip));
        this.createTariffLineItemImages(defaultAdjustTLITooltip, defaultCopyTLITooltip);
        this.tableTariffLineItem.addCenterTool(this.editTariffLineItemImage);
        this.tableTariffLineItem.addCenterTool(this.adjustRatesTariffLineItemImage);
        this.tableTariffLineItem.addCenterTool(this.copyTariffLineItemImage);
        this.tableTariffLineItem.addRightTool(this.buttonAddTariffLineItem);
    }

    private enableActionItemImageAndScrollElementIntoView() {
        const tableTariffLineItem: Table = this.tableTariffLineItem;
        tableTariffLineItem.addSelectionListener(async () => {
            if (tableTariffLineItem.hasSelection) {
                const selectedRow: TableRow = tableTariffLineItem.selectedRow;
                if (selectedRow) {
                    DOMUtil.scrollElementIntoView(selectedRow._element, {block: Block.END}, tableTariffLineItem._element);
                }
            }
        });
    }

    private showEmptyColumns() {
        const modelRows: ModelRow[] = this.sourceTariffLineItem?.data;
        if (modelRows.length == 0) {
            TariffManagementUtil.showColumnsForEmptyData(this.tableTariffLineItem, false);
        }
    }

    private updateImageTooltipAndState(defaultCopyTLITooltip: string, defaultAdjustTLITooltip: string) {
        const numberOfRowsSelected = this.tableTariffLineItem.selectedRowCount;
        this.editTariffLineItemImage.enabled = numberOfRowsSelected == 1 ? true : false;
        this.editTariffLineItemImage.color = numberOfRowsSelected == 1 ? 'primary' : 'default.lightest';
        this.copyTariffLineItemImage.enabled = numberOfRowsSelected > 0;
        this.copyTariffLineItemImage.color = numberOfRowsSelected > 0 ? 'primary' : 'default.lightest';
        this.adjustRatesTariffLineItemImage.enabled = numberOfRowsSelected > 0;
        this.adjustRatesTariffLineItemImage.color = numberOfRowsSelected > 0 ? 'primary' : 'default.lightest';
        const copyTooltip =
            numberOfRowsSelected === 0
                ? defaultCopyTLITooltip
                : numberOfRowsSelected === 1
                    ? "Copy 1 selected Tariff Line Item"
                    : `Copy ${numberOfRowsSelected} selected Tariff Line Items`;
        this.copyTariffLineItemImage.tooltip = copyTooltip;
        const adjustTooltip =
            numberOfRowsSelected === 0
                ? defaultAdjustTLITooltip
                : numberOfRowsSelected === 1
                    ? "Adjust Rate 1 selected Tariff Line Item"
                    : `Adjust Rate ${numberOfRowsSelected} selected Tariff Line Items`;
        this.adjustRatesTariffLineItemImage.tooltip = adjustTooltip;
        this.editTariffLineItemImage.tooltip = numberOfRowsSelected === 1 ? 'Update selected Tariff Line Item' : null;
    }

    private createTariffLineItemImages(defaultAdjustTLITooltip: string, defaultCopyTLITooltip: string) {
        this.buttonAddTariffLineItem = new Button({
            caption: "Add Tariff Line Item",
            color: "primary",
            enabled: false,
            imageName: ImageName.add,
            onClick: () => this.addTariffLineItemSlideout()
        });
        this.editTariffLineItemImage = new Image({
            name: ImageName.pencil,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: 'Update selected Tariff Line Item',
            enabled: false,
            marginRight: 16,
            onClick: () => this.updateTariffLineItemSlideout()
        });
        this.adjustRatesTariffLineItemImage = new Image({
            name: ImageName.adjustRates,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: defaultAdjustTLITooltip,
            enabled: false,
            marginRight: '16',
            onClick: () => this.adjustRateTariffLineItemSlideout()
        });
        this.copyTariffLineItemImage = new Image({
            name: ImageName.copy,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: defaultCopyTLITooltip,
            enabled: false,
            onClick: () => this.copyTariffLineItemSlideout()
        });
    }

    private addTariffLineItemSlideout() {
        const layout = Layout.getLayout("lme/dispatch/TariffManagementTariffLineItem") as TariffManagementTariffLineItem;
        const tarUID = this.mainDataSource?.lastSearch?.parentLink?.tar_uid;
        new EditRowDecorator({
            title: "Add Tariff Line Item",
            layout: layout,
            fillVerticalSpace: true,
            width: '70%',
            dataSource: layout.mainDataSource,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.addUpdateTariffLineItemButton(true, tarUID, layout),
            layoutLoadListeners: async () => {
                layout.textboxCopyToTariff.visible = false;
                layout.textboxCopyToTariff.required = false;
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                layout.activeRow.set("tar_uid", tarUID);
            },
            doAfterSlideIn: () => {
                layout.textboxCopyToTariff.printable = true;
            },
            onClose: async (cancelled: boolean) => {
                this.validateUnsaveChanges(layout, tarUID, false);
            },
        });
    }

    private updateTariffLineItemSlideout() {
        const layout = Layout.getLayout("lme/dispatch/TariffManagementTariffLineItem") as TariffManagementTariffLineItem;
        const selectedRow = this.tableTariffLineItem.selectedRow;
        const tarUID = this.mainDataSource?.lastSearch?.parentLink?.tar_uid;
        if (selectedRow == null) {
            Snackbar.showWarningSnackbar("Please select the Table row to perform operation.")
            return;
        }
        const modelRow: ModelRow = selectedRow?.data;
        const tariffLineItemUniqueId = modelRow?.get("tli_uid");
        new EditRowDecorator({
            title: `Edit Tariff Line Item - ${tariffLineItemUniqueId}`,
            layout: layout,
            fillVerticalSpace: true,
            width: '70%',
            dataSource: layout.mainDataSource,
            data: modelRow,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.addUpdateTariffLineItemButton(false, tarUID, layout),
            layoutLoadListeners: async () => {
                layout.textboxCopyToTariff.visible = false;
                layout.textboxCopyToTariff.required = false;
                layout.populateLookupModelDataInSlider(modelRow, false);
                layout.addCodeRowsAndTariffRegionsQuickInfo();
            },
            doAfterSlideIn: () => {
                layout.textboxCopyToTariff.printable = false;
            },
            onClose: async (cancelled: boolean) => {
                this.validateUnsaveChanges(layout, tarUID, false);
            },
        });
    }

    adjustRateTariffLineItemSlideout(): void {
        const selectedTableRows: ModelRow[] = this.tableTariffLineItem.selectedRows.map((tableRow: TableRow) => tableRow.data);
        const selectedRowsLength = selectedTableRows.length;
        const layout = this.adjustRateTLISlideoutLayout;
        const title = `Adjust Rates (${selectedRowsLength})`;
        layout.selectedRows = selectedTableRows;
        new EditRowDecorator({
            title: title,
            layout: layout,
            fillVerticalSpace: true,
            width: '60%',
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.saveButton,
            doAfterSlideIn: () => {
                this.saveButton.enabled = false;
                this.saveButton.busy = false;
                layout.saveButton = this.saveButton;
                layout.textboxAdjustRateByValue.focus();
            },
            onClose: async (cancelled: boolean) => {
                const tarUID = this.mainDataSource?.lastSearch?.parentLink?.tar_uid;
                const tableLengthActive = layout.layoutTariffLineItemDataActive.tableTLIData.data?.length > 0;
                const tableLengthExpire = layout.layoutTariffLineItemDataExpire.tableTLIData.data?.length > 0;
                if (tableLengthActive || tableLengthExpire) {
                    const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
                    const dialogProps = {yesButtonCaption: "Leave",noButtonCaption: "Stay"};
                    const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                    if (leave === true) {
                        layout.setDefaultLayoutValues();
                        layout.saveButton.enabled = false;
                        layout.slideOut();
                        await this.mainDataSource.search({ tar_uid: tarUID });
                    }
                } else {
                    layout.setDefaultLayoutValues();
                    layout.saveButton.enabled = false;
                    layout.slideOut();
                    await this.mainDataSource.search({ tar_uid: tarUID });
                }
            },
        });
    }

    private async copyTariffLineItemSlideout(): Promise<void> {
        const selectedTableRows: ModelRow[] = this.tableTariffLineItem.selectedRows.map((tableRow: TableRow) => tableRow.data);
        const selectedRowsLength = selectedTableRows.length;
        const tarUID = this.mainDataSource?.lastSearch?.parentLink?.tar_uid;
        const layout = Layout.getLayout("lme/dispatch/TariffManagementTariffLineItem") as TariffManagementTariffLineItem;
        const title = `Copy Tariff Line Item${selectedRowsLength !== 1 ? 's' : ''} (${selectedRowsLength})`;
        const response = await Api.search("lme/dispatch/tariff-line-item-copy-rows", { tariff_line_items: selectedTableRows, from_tariff_uid: tarUID});
        if (!response?.data[0]) return;
        const data = response?.data[0];
        const editFields = data?.editable_fields;
        const exludeFields = data?.exclude_fields;
        const fromTariffInfo = data?.from_tariff_info;
        layout.setFromTariffTypeCode = fromTariffInfo.tariff_type_code;
        layout.fromTariffUID = fromTariffInfo.tar_uid;
        layout.fromTariffID = fromTariffInfo.tariff_id;
        const sliderRow = data?.slider_row;
        const modelRowFromResponse = new ModelRow(this.sourceTariffLineItem.url, true, sliderRow);
        new EditRowDecorator({
            title: title,
            layout: layout,
            fillVerticalSpace: true,
            width: '70%',
            dataSource: layout.mainDataSource,
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            addlComponents: this.copyTariffLineItemButton(layout, selectedTableRows, editFields, exludeFields),
            layoutLoadListeners: async () => {
                layout.textboxCopyToTariff.visible = true;
                layout.textboxCopyToTariff.required = true;
                layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, [modelRowFromResponse]);
                const textboxes = this.tariffLineItemSliderComponents(layout);
                textboxes.forEach(({ field, slideOutComponent }) => { slideOutComponent.enabled = !exludeFields.includes(field);});
                layout.populateLookupModelDataInSlider(modelRowFromResponse, true);
                layout.addCodeRowsAndTariffRegionsQuickInfo(true);
                layout.mainDataSource.displayDataInBoundComponents();
            },
            doAfterSlideIn: () => {
                layout.textboxCopyToTariff.printable = false;
            },
            onClose: async (cancelled: boolean) => {
                this.validateUnsaveChanges(layout, tarUID, true);
            },
        });
    }

    private addUpdateTariffLineItemButton(isAddMode: boolean, tarUID: any, layout: TariffManagementTariffLineItem): Button {
        const caption = isAddMode ? "Add" : "Update";
        const addUpdateButton = new Button({
            backgroundColor: "primary",
            borderColor: "primary",
            borderWidth: 1,
            caption: caption,
            color: "primary.reverse",
            rowBreak: false,
            onClick: async () =>  {
                try {
                    addUpdateButton.busy = true;
                    const isDataSourceChanged = layout.mainDataSource.hasChanged();
                    if (isDataSourceChanged === false) {
                        CommonDialogs.showError("No changes were made. Please try again or check your input.");
                        return;
                    }
                    const activeRow: ModelRow = layout.activeRow;
                    const response = await Api.search("lme/dispatch/tariff-line-item-duplicate-check", { "tariff_line_item": activeRow, is_add: isAddMode, tariff_management_id: tarUID});
                    if (response) {
                        const data = response.data[0];
                        const isDuplicate = data.is_duplicate;
                        if (isDuplicate) {
                            const message = data.message;
                            const dialogProps = { yesButtonCaption: "Yes", noButtonCaption: "No" };
                            const selectionYes = await CommonDialogs.showYesNo(message, "Action Confirmation", dialogProps);
                            if (selectionYes === true) {
                                try {
                                    await this.addUpdateTLI(layout, isAddMode, tarUID);
                                } catch (error) {
                                    CommonDialogs.showError("Unable to Post/Update Record to Tariff Line Item.");
                                } finally {
                                    addUpdateButton.busy = false;
                                }
                            } else {
                                addUpdateButton.busy = false;
                            }
                        } else {
                            try {
                                await this.addUpdateTLI(layout, isAddMode, tarUID);
                            } catch (error) {
                                CommonDialogs.showError("Unable to Post/Update Record to Tariff Line Item.");
                            } finally {
                                addUpdateButton.busy = false;
                            }
                        }
                    } else {
                        CommonDialogs.showError("Unable to check for duplicates in the tariff line item.");
                        addUpdateButton.busy = false;
                    }
                } catch (error) {
                    CommonDialogs.showError(error);
                }
                finally {
                    addUpdateButton.busy = false;
                }
            }
        });
        return addUpdateButton;
    }

    private async addUpdateTLI(layout: TariffManagementTariffLineItem, isAddMode: boolean, tarUID: any) {
        try {
            const response = await layout.mainDataSource.post();
            if (response) {
                const message = isAddMode ? "Tariff Line Item added" : "Tariff Line Item updated";
                Toast.showToast(message);
                await this.delay(2000);
                await this.reloadTariffLineItem(layout, tarUID, false);
            }
        } catch (error) {
            CommonDialogs.showError(error);
        }
    }

    private async reloadTariffLineItem(layout: TariffManagementTariffLineItem, tarUID: number, forCopyTariffLineItem: boolean) {
        layout.slideOut();
        await this.mainDataSource.search({ tar_uid: tarUID });
        this.editTariffLineItemImage.enabled = false;
        this.editTariffLineItemImage.color = 'default.lightest';
        this.resetManuallyAddTooltip(layout);
        if (forCopyTariffLineItem) {
            const slideoutComponents = this.tariffLineItemSliderComponents(layout);
            slideoutComponents.forEach(component => {
                const textboxOrCheckbox = component.slideOutComponent;
                if (textboxOrCheckbox instanceof Textbox) {
                    textboxOrCheckbox.enabled = true;
                    textboxOrCheckbox.text = null;
                    textboxOrCheckbox["lookupModelData"] = null;
                } else if (textboxOrCheckbox instanceof Checkbox) {
                    textboxOrCheckbox.enabled = true;
                    textboxOrCheckbox.checked = false;
                }
            });
        }
    }

    private async validateUnsaveChanges(layout: TariffManagementTariffLineItem, tarUID: any, forCopyTariffLineItem: boolean) {
        if (!layout.mainDataSource.hasChanged()) {
            await this.reloadTariffLineItem(layout, tarUID, forCopyTariffLineItem);
            return;
        }
        const message = "Unsaved changes are present.  Do you wish to leave without saving changes?";
        const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
        const leaveStaySelection = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
        if (leaveStaySelection == true) {
            await this.reloadTariffLineItem(layout, tarUID, forCopyTariffLineItem);
        }
    }

    private resetManuallyAddTooltip(layout: TariffManagementTariffLineItem) {
        const textboxes = [layout.textboxMethod, layout.textboxConveyanceTypeCode, layout.textboxNmfc, layout.textboxQualFactor1, layout.textboxCalcFactor2, layout.textboxCompareOptionCode];
        textboxes.forEach(textbox => { textbox.text = null; });
    }

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private saveButton: SaveButton = SaveButton.create({
        id: "applyButton",
        caption: "Save",
        rowBreak: false,
        enabled: false,
        onClick: async (event: ClickEvent) => {
            const layout = this.adjustRateTLISlideoutLayout as TariffManagementTLIAdjustRates;
            layout.saveButton.busy = true;
            layout.buttonClear.enabled = false;
            const tarUID = this.mainDataSource?.lastSearch?.parentLink?.tar_uid;
            const selectedRows = layout.selectedRows as ModelRow[];
            const tableLengthActive = layout.layoutTariffLineItemDataActive.tableTLIData.data?.length > 0;
            const tableLengthExpire = layout.layoutTariffLineItemDataExpire.tableTLIData.data?.length > 0;
            if (tableLengthActive || tableLengthExpire) {
                const message = "Adjusting rates may affect Tariff Line Items. Would you like to proceed with changes?";
                const dialogProps = {yesButtonCaption: "Yes",noButtonCaption: "No"};
                const userSelection = await CommonDialogs.showYesNo(message, "Proceed with Changes?", dialogProps);
                if (userSelection === true) {
                    const tariffInfoObject = layout.buildAdjustRatesObject();
                    if (tariffInfoObject === null) {
                        layout.saveButton.busy = false;
                        layout.buttonClear.enabled = true;
                        return;
                    }
                    const effecttiveDate = layout.textboxEffectiveDate.text;
                    if (StringUtil.isEmptyString(effecttiveDate)) {
                        Snackbar.showWarningSnackbar("Provide Effective Date when adjusting Tariff Line Item(s).");
                        layout.saveButton.busy = false;
                        layout.buttonClear.enabled = true;
                        return;
                    }
                    const response = await Api.post("lme/dispatch/tariff-line-item-adjust-rates", {
                        "selected_rows": selectedRows,
                        "adjust_rates_info": tariffInfoObject,
                        "effective_date": effecttiveDate
                    });
                    if (response) {
                        const message = response.data[0].message;
                        const succes = response.data[0].success;
                        if (succes) {
                            Toast.showToast(message);
                            await this.delay(2000);
                            layout.setDefaultLayoutValues();
                            layout.saveButton.enabled = false;
                            layout.saveButton.busy = false;
                            layout.buttonClear.enabled = false;
                            layout.slideOut();
                            await this.mainDataSource.search({ tar_uid: tarUID });
                        }
                    }
                } else {
                    Snackbar.showWarningSnackbar("All Rates Adjustment have been cancelled");
                    layout.setDefaultLayoutValues();
                    layout.saveButton.enabled = false;
                    layout.saveButton.busy = false;
                    layout.buttonClear.enabled = false;
                    layout.slideOut();
                    await this.mainDataSource.search({ tar_uid: tarUID });
                }
            } else {
                layout.saveButton.enabled = false;
                layout.saveButton.busy = false;
                layout.buttonClear.enabled = false;
            }
        }
    })

    private copyTariffLineItemButton(layout: TariffManagementTariffLineItem, selectedTableRows: ModelRow[], editableFields: any, exludeFields: any): Button {
        return new Button({
            backgroundColor: "primary",
            borderColor: "primary",
            borderWidth: 1,
            caption: "Copy",
            color: "primary.reverse",
            rowBreak: false,
            onClick: async () =>  {
                const fromTariffUID = layout.fromTariffUID;
                const fromTariffID = layout.fromTariffID;
                const copyApiResponse = await this.copyTariffLineItems(fromTariffUID, fromTariffID, selectedTableRows, editableFields, exludeFields, layout, true, false);
                const data = copyApiResponse?.data[0];
                const copyRowsDuplicate = data.copy_rows_duplicates;
                if (copyRowsDuplicate.length > 0) {
                    const tliUIDs = copyRowsDuplicate.map(item => `\t• ${item.tli_uid}`).join('\n');
                    const message = "There are " + copyRowsDuplicate.length + " Duplicate TLIs.\n\n" + tliUIDs + "\n\nContinue Anyway?";
                    const dialogProps = { yesButtonCaption: "Yes", noButtonCaption: "No" };
                    const selectionYes = await CommonDialogs.showYesNo(message, "Action Confirmation", dialogProps);
                    if (selectionYes === true) {
                        const postCopyTLIRows = await this.copyTariffLineItems(fromTariffUID, fromTariffID, selectedTableRows, editableFields, exludeFields, layout, false, true);
                        const data = postCopyTLIRows?.data[0];
                        if (data) {
                            Toast.showToast(data.message);
                            await this.delay(2000);
                            await this.reloadTariffLineItem(layout, fromTariffUID, true);
                        }
                    }
                } else {
                    const postCopyTLIRows = await this.copyTariffLineItems(fromTariffUID, fromTariffID, selectedTableRows, editableFields, exludeFields, layout, false, true);
                    const data = postCopyTLIRows?.data[0];
                    if (data) {
                        Toast.showToast(data.message);
                        await this.delay(2000);
                        await this.reloadTariffLineItem(layout, fromTariffUID, true);
                    }
                }
            }
        });
    }

    private async copyTariffLineItems(fromTariffUID: number, fromTariffID: string, selectedTableRows: ModelRow<any>[], editableFields: any, exludeFields: any, layout: TariffManagementTariffLineItem, checkDuplicate: boolean, postData: boolean) {
        return await Api.post("lme/dispatch/tariff-line-item-copy-rows", {
            from_tariff_uid: fromTariffUID,
            from_tariff_id: fromTariffID,
            tariff_line_items: selectedTableRows,
            edit_fields: editableFields,
            exclude_fields: exludeFields,
            active_row: layout.activeRow,
            check_duplicate: checkDuplicate,
            post_data: postData
        });
    }

    tableTariffLineItemOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        TariffManagementUtil.tableRowDisplayLogic(event);
        this.addDoubleClickListenerToUpdateTariffLineItem(tableRow);
        if (this._tliUid && this._tliUid == modelRow.get("tli_uid")) {
            this.tableTariffLineItem.selectRow(tableRow, SelectionType.SINGLE, null);
        }
    }

    private addDoubleClickListenerToUpdateTariffLineItem(tableRow: TableRow) {
        tableRow.addDblClickListener(() => this.updateTariffLineItemSlideout());
    }

    sourceTariffLineItemAfterExecution() {
        this.tariffLineItemPanel.toggleFilterById("active");
        const isTarUniquePresent = this.mainDataSource?.lastSearch?.parentLink?.tar_uid;
        this.buttonAddTariffLineItem.enabled = !StringUtil.isEmptyString(isTarUniquePresent) ? true : false;
        const modelRows: ModelRow[] = this.sourceTariffLineItem?.data;
        if (modelRows.length == 0) {
            TariffManagementUtil.showColumnsForEmptyData(this.tableTariffLineItem, false);
        } else {
            TariffManagementUtil.dynamicallyShowColumn(this.tableTariffLineItem, modelRows);
        }
    }

    private tariffLineItemSliderComponents(layout: TariffManagementTariffLineItem) {
        return [
            { field: "gla_uid", slideOutComponent: layout.textboxGlaUid },
            { field: "service_type_code", slideOutComponent: layout.textboxServiceTypeCode },
            { field: "carrier_scac", slideOutComponent: layout.textboxCarrierScac },
            { field: "ship_reg_name", slideOutComponent: layout.textboxShipRegName },
            { field: "cons_reg_name", slideOutComponent: layout.textboxConsRegName },
            { field: "miles", slideOutComponent: layout.textboxMiles },
            { field: "weight", slideOutComponent: layout.textboxWeight },
            { field: "pieces", slideOutComponent: layout.textboxPieces },
            { field: "spots", slideOutComponent: layout.textboxSpots },
            { field: "average_pallet_weight", slideOutComponent: layout.textboxAveragePalletWeight },
            { field: "handling_units", slideOutComponent: layout.textboxHandlingUnits },
            { field: "freight_value", slideOutComponent: layout.textboxFreightValue },
            { field: "linear_units", slideOutComponent: layout.textboxLinearUnits },
            { field: "cubic_units", slideOutComponent: layout.textboxCubicUnits },
            { field: "nmfc", slideOutComponent: layout.textboxNmfc },
            { field: "product_sku", slideOutComponent: layout.textboxProductSku },
            { field: "conveyance_type_code", slideOutComponent: layout.textboxConveyanceTypeCode },
            { field: "qual_factor_1", slideOutComponent: layout.textboxQualFactor1 },
            { field: "qual_factor_2", slideOutComponent: layout.textboxQualFactor2 },
            { field: "priority", slideOutComponent: layout.textboxPriority },
            { field: "effective_timestamp", slideOutComponent: layout.textboxEffectiveTimestamp },
            { field: "expire_timestamp", slideOutComponent: layout.textboxExpireTimestamp },
            { field: "calc_uom_code", slideOutComponent: layout.textboxMethod },
            { field: "rate", slideOutComponent: layout.textboxRate },
            { field: "calc_factor", slideOutComponent: layout.textboxCalcFactor },
            { field: "calc_factor_2", slideOutComponent: layout.textboxCalcFactor2 },
            { field: "minimum_charge", slideOutComponent: layout.textboxMinimumCharge },
            { field: "maximum_charge", slideOutComponent: layout.textboxMaximumCharge },
            { field: "rate_note", slideOutComponent: layout.textboxRateNote },
            { field: "reversible_flag", slideOutComponent: layout.checkboxRateEitherDirection },
            { field: "charge_regardless_flag", slideOutComponent: layout.checkboxChargeRegardlessFlag },
        ];
    }
}
